export interface Role {
  id: string;
  company: { name: string; logoUrl?: string; atSacc?: boolean };
  title: string;
  employmentType: 'Internship' | 'Graduate' | 'Full-time' | 'Not specified';
  employmentTypes?: Array<'Internship' | 'Graduate' | 'Full-time'>;
  eligibility: string;
  deadline: string | null;
  location: string;
  field?: string;
  description: string;
  published: boolean;
  applicationRoute: 'direct' | 'external';
  applicationUrl?: string;
}

export function safeApplicationUrl(value?: string): boolean {
  if (!value || /\s/.test(value)) return false;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && Boolean(url.hostname) && !url.username && !url.password;
  } catch { return false; }
}

export function applicationPath(role: Role): string {
  return `/cfas/hub/sign-in?nav=${encodeURIComponent('/cfas/apply/submit')}&roleID=${encodeURIComponent(role.id)}`;
}

export function isAvailable(role: Role, now = Date.now()): boolean {
  return role.published === true && Boolean(role.id && role.title && role.company?.name && role.eligibility) &&
    ['Internship', 'Graduate', 'Full-time', 'Not specified'].includes(role.employmentType) && (role.deadline === null || Date.parse(role.deadline) > now) &&
    (role.applicationRoute === 'direct' || (role.applicationRoute === 'external' && safeApplicationUrl(role.applicationUrl)));
}

export function formatDeadline(deadline: string | null): string {
  if (deadline === null) return 'No closing date specified';
  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Europe/London' }).format(new Date(deadline));
}

// Opt-in fixtures for frontend review only. Never used as an API error fallback.
const previewRoles: Role[] = [
  { id: 'preview-engineer', field: 'Engineering', company: { name: 'Example Engineering', atSacc: true }, title: 'Graduate Engineer', employmentType: 'Graduate', eligibility: 'Final year or recent graduate in engineering', deadline: '2027-11-05T23:59:59Z', location: 'Bangkok, Thailand', description: 'Example opportunity for frontend preview. Join an engineering team to work on sustainable manufacturing and process improvement.', published: true, applicationRoute: 'direct' },
  { id: 'preview-intern', field: 'Technology', company: { name: 'Example Digital' }, title: 'Software Engineering Intern', employmentType: 'Internship', eligibility: 'Current students in computer science or a related course', deadline: '2027-11-12T23:59:59Z', location: 'Bangkok, Thailand', description: 'Example opportunity for frontend preview. Collaborate with developers on web applications and cloud services.', published: true, applicationRoute: 'external', applicationUrl: 'https://example.com/careers' },
  { id: 'preview-analyst', field: 'Business and consulting', company: { name: 'Example Consulting', atSacc: true }, title: 'Business Analyst', employmentType: 'Full-time', eligibility: 'Graduates from any course', deadline: '2027-11-18T23:59:59Z', location: 'London, UK', description: 'Example opportunity for frontend preview. Research business challenges and help teams turn insights into practical recommendations.', published: true, applicationRoute: 'direct' },
];

export const previewEnabled = process.env.REACT_APP_ROLES_PREVIEW === 'true';

export async function fetchPublicRoles(signal: AbortSignal): Promise<Role[]> {
  if (previewEnabled) return previewRoles;
  const url = process.env.REACT_APP_PUBLIC_ROLES_URL;
  if (!url) throw new Error('The roles service is not connected yet. Please check back soon.');
  const response = await fetch(url, { signal, credentials: 'omit' });
  if (!response.ok) throw new Error('We could not load roles. Please try again.');
  const body = await response.json();
  if (!Array.isArray(body.roles)) throw new Error('We could not load roles. Please try again.');
  return body.roles.filter((role: Role) => role && isAvailable(role));
}
